import React from 'react';
import type { GoalMetric, GoalResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import {
  ASSIGNEE_SUBLABEL,
  METRIC_ICON,
  METRIC_LABEL,
  PERIOD_LABEL,
  formatPeriodRange,
  formatTarget,
  getAssigneeInitials,
  isCompanyGoal,
} from '../utils/goalDisplay';

/**
 * Card de meta (view "Cards" + fallback mobile). Padrão-lista: card flat/borda
 * SEM checkbox (F34 — seleção em massa só na tabela) + menu ••• de ações.
 * Avatar sem foto = slate.
 */
export interface GoalCardProps {
  goal: GoalResponse;
  onEdit?: (goal: GoalResponse) => void;
  onDelete?: (goal: GoalResponse) => void;
  /** "Ver progresso" — navega pro dashboard comercial */
  onViewProgress?: (goal: GoalResponse) => void;
  /** "Duplicar" — abre o drawer pré-preenchido pro próximo período */
  onDuplicate?: (goal: GoalResponse) => void;
}

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  onEdit,
  onDelete,
  onViewProgress,
  onDuplicate,
}) => {
  const metric: GoalMetric = goal.metric;
  const assigneePrimary = goal.assigneeName ?? (isCompanyGoal(goal) ? 'Empresa' : '—');
  const assigneeIcon: IconName | null = isCompanyGoal(goal)
    ? 'globe'
    : goal.assigneeType === 'team'
      ? 'users'
      : null;

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(goal) });
  if (onViewProgress) actions.push({ label: 'Ver progresso', icon: 'trending-up', onClick: () => onViewProgress(goal) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(goal) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(goal), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`goals-card-${goal.id}`}
    >
      {/* Topo: avatar + responsável + menu */}
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            {assigneeIcon ? (
              <Icon name={assigneeIcon} className="w-5 h-5 text-white" />
            ) : (
              <span className="text-white font-semibold text-sm">{getAssigneeInitials(goal)}</span>
            )}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {assigneePrimary}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {ASSIGNEE_SUBLABEL[goal.assigneeType]}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`goals-card-actions-${goal.id}`} />
          </div>
        )}
      </div>

      {/* Métrica + Período */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center gap-1.5 h-[24px] px-2.5 rounded-lg text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={METRIC_ICON[metric]} className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          {METRIC_LABEL[metric]}
        </span>
        <span
          className="inline-flex items-center h-[24px] px-2.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          {PERIOD_LABEL[goal.period] ?? goal.period}
        </span>
      </div>

      {/* Valor-alvo */}
      <div className="mt-3 flex items-baseline justify-between gap-2">
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Meta</span>
        <span className="font-bold text-base tabular-nums truncate" style={{ color: 'var(--theme-text)' }}>
          {formatTarget(metric, goal.targetValue)}
        </span>
      </div>

      {/* Footer: janela (range de datas) */}
      <div
        className="mt-3 pt-3 text-xs truncate"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        {formatPeriodRange(goal.periodStart, goal.periodEnd)}
      </div>
    </div>
  );
};

GoalCard.displayName = 'GoalCard';
